import { useCallback, useEffect, useRef, useState } from "react";
import { api, ApiError } from "../../../api/client.js";

/**
 * FIX-080 (owner t1551u / t1552u): REEL - Research US Score. Every Reel of the run's Top 30 gets the Deep Research US Score (same Gemini prompt and
 * model, scored on the server automatically after a run finishes, within a daily cap); the Top 30 is ranked by it. Isolated in this file: the page only
 * passes the scores down and sorts with sortReelsByUsScore. Reads GET .../runs/:runId/us-scores; the "Score US" button is the manual fallback
 * (POST, owner / operator only). A failed read shows nothing (the list keeps the Relative Performance order).
 */
export type UsTier = "US" | "Global" | "Not US";
export interface ReelUsScoreView {
  readonly reelId: string;
  readonly shortcode: string;
  readonly usScore: number;
  readonly tier: UsTier;
  readonly why: string;
  readonly source: "gemini" | "deep_research";
  readonly scoredAt: string;
  /** FIX-080 (t1559u): on-camera speech from the same US score answer; null / missing = unknown. */
  readonly hasSpeech?: boolean | null;
}
export interface ReelUsStatus {
  readonly scores: readonly ReelUsScoreView[];
  readonly pending: number;
  readonly failed: number;
  readonly configured: boolean;
  readonly capUsd: number;
  readonly spentTodayUsd: number;
  readonly capReached: boolean;
  readonly estimateUsd: number;
  readonly running: boolean;
}

const POLL_MS = 15_000;
const MAX_POLLS = 40; // ~10 minutes while Reels are still waiting for a score

/**
 * Owner t1559u (on top of t1552u): Reels WITH on-camera speech first, then without speech, then speech unknown (not scored yet); inside each group
 * US score highest first, unscored last; ties keep the given (Relative Performance) order. Same rule as the API's sortReelsBySpeechAndUsScore.
 */
export function sortReelsByUsScore<T extends { readonly reelId: string }>(reels: readonly T[], scores: Readonly<Record<string, ReelUsScoreView>> | null): T[] {
  if (scores === null) return [...reels];
  const group = (h: boolean | null | undefined): number => (h === true ? 0 : h === false ? 1 : 2);
  return reels.map((r, i) => ({ r, i, g: group(scores[r.reelId]?.hasSpeech), s: scores[r.reelId]?.usScore ?? null })).sort((a, b) => {
    if (a.g !== b.g) return a.g - b.g;
    if (a.s !== null && b.s !== null && a.s !== b.s) return b.s - a.s;
    if (a.s === null && b.s !== null) return 1;
    if (a.s !== null && b.s === null) return -1;
    return a.i - b.i;
  }).map((x) => x.r);
}

export function useReelUsScores(base: string, personaId: string, runId: string | null, csrfToken: string | null) {
  const [status, setStatus] = useState<ReelUsStatus | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [requesting, setRequesting] = useState(false);
  const polls = useRef(0);
  const url = runId === null ? null : `${base}/personas/${encodeURIComponent(personaId)}/runs/${encodeURIComponent(runId)}/us-scores`;

  const load = useCallback(async (): Promise<ReelUsStatus | null> => {
    if (url === null) return null;
    try {
      const s = await api.get<ReelUsStatus>(url);
      setStatus(s);
      return s;
    } catch {
      return null;
    }
  }, [url]);

  useEffect(() => {
    setStatus(null);
    setMessage(null);
    polls.current = 0;
    if (url === null) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const tick = async () => {
      const s = await load();
      if (cancelled || s === null) return;
      const waiting = s.configured && !s.capReached && (s.running || s.pending > 0);
      if (waiting && polls.current++ < MAX_POLLS) timer = setTimeout(() => void tick(), POLL_MS);
    };
    void tick();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [url, load]);

  const scoreNow = useCallback(async () => {
    if (url === null) return;
    setRequesting(true);
    setMessage(null);
    try {
      const r = await api.post<{ started: boolean; reason: string | null }>(url, csrfToken);
      setMessage(r.started ? "US scoring started - scores appear here as they arrive." : r.reason === "already_running" ? "US scoring is already running for this run." : r.reason === "another_run_in_progress" ? "US scoring is busy with another run - try again in a few minutes." : r.reason === "not_configured" ? "US score is not configured on this server." : `US scoring did not start (${r.reason ?? "unknown"}).`);
      polls.current = 0;
      const tick = async (n: number): Promise<void> => {
        const s = await load();
        if (s !== null && (s.running || s.pending > 0) && n < MAX_POLLS) await new Promise((res) => setTimeout(res, POLL_MS)).then(() => tick(n + 1));
      };
      void tick(0);
    } catch (e) {
      const body = e instanceof ApiError ? (e.body as { message?: string } | null) : null;
      setMessage(body?.message ?? "Could not start US scoring.");
    } finally {
      setRequesting(false);
    }
  }, [url, csrfToken, load]);

  const byReel: Readonly<Record<string, ReelUsScoreView>> | null = status === null ? null : Object.fromEntries(status.scores.map((s) => [s.reelId, s]));
  return { status, byReel, scoreNow, requesting, message };
}

/** One line per Reel inside the Top 30 card. */
export function ReelUsScoreLine({ score }: { readonly score: ReelUsScoreView | undefined }) {
  return (
    <p style={{ margin: "4px 0" }} data-testid="top-reel-us">
      US score:{" "}
      {score === undefined ? (
        <span className="muted" data-testid="top-reel-us-pending">not scored yet</span>
      ) : (
        <>
          <strong data-testid="top-reel-us-score">{score.usScore}</strong> <span data-testid="top-reel-us-tier">({score.tier})</span>
          {" · "}<span data-testid="top-reel-us-speech">{score.hasSpeech === true ? "speech" : score.hasSpeech === false ? "no speech" : "speech unknown"}</span>
          {score.why ? <span className="muted"> — {score.why}</span> : null}
        </>
      )}
    </p>
  );
}

/** The status line above the Top 30 and the manual "Score US" fallback. */
export function ReelUsScoreBar({ us, canAct }: { readonly us: ReturnType<typeof useReelUsScores>; readonly canAct: boolean }) {
  const s = us.status;
  if (s === null) return null;
  const scored = s.scores.length;
  const total = scored + s.pending;
  return (
    <p data-testid="reel-us-bar" className="muted">
      Reels with on-camera speech first, then without speech; inside each, ranked by US score (fit to a US audience, 0-100; US 70+, Global 45-69, Not US under 45), highest first. {scored} of {total} Reels scored
      {s.running ? " · scoring now…" : ""}
      {s.failed > 0 ? ` · ${s.failed} could not be scored yet` : ""}
      {!s.configured ? " · US score is not configured on this server" : ""}
      {s.capReached ? ` · today's US score budget ($${s.capUsd.toFixed(2)}) is used up` : ""}.
      {canAct && s.configured && s.pending > 0 && !s.running ? (
        <>
          {" "}
          <button type="button" data-testid="reel-us-score-now" disabled={us.requesting || s.capReached} onClick={() => void us.scoreNow()}>
            Score US
          </button>{" "}
          <span>(about ${s.estimateUsd.toFixed(2)})</span>
        </>
      ) : null}
      {us.message !== null ? <span data-testid="reel-us-message"> {us.message}</span> : null}
    </p>
  );
}
